import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Save, Loader2, CheckCircle2, AlertCircle, Lock,
  MessageSquare, GraduationCap, Sparkles, FileSearch,
  Camera, Star, Info, Cpu, Network, ClipboardList, Mic2,
} from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { AI_MODELS, MODEL_BY_ID, modelsForSurface, SURFACE_REQUIREMENTS } from '../../../lib/aiModels';
import {
  INSPECTION_SLOT_KEYS,
  REASONING_EFFORTS,
} from '../../../lib/inspectionSettings';
import { useInspectionSettings } from '../../../contexts/InspectionSettingsContext';
import { ModelLogo } from '../../admin/ModelLogos';
import { toFa } from '../../../utils/faFormat';
import '../../../styles/ModelSettingsPanel.css';

// Per-surface "thinking depth". 'adaptive' = the model decides (light for simple
// asks, deep for hard ones); 'none' = thinking OFF. Order = how it renders.
const EFFORT_FA = {
  adaptive: 'هوشمند (خودکار)',
  none: 'بدون تفکر',
  low: 'کم',
  medium: 'متوسط',
  high: 'زیاد',
  xhigh: 'خیلی زیاد',
  max: 'حداکثر',
};
const EFFORTS_FALLBACK = [...REASONING_EFFORTS];
export const SUBMODEL_LABELS = {
  'gpt-5.5': 'GPT-5.5',
  'gpt-6-astra': 'GPT 6 Astra',
  'gpt-6.1-sol': 'GPT 6.1 Sol',
  'gpt-6.1-terra': 'GPT 6.1 Terra',
  'gpt-6-sol': 'GPT 6 Sol',
  'gpt-6-luna': 'GPT 6 Luna',
  'gpt-5.6-sol': 'GPT 5.6 Sol',
  'gpt-5.6-terra': 'GPT 5.6 Terra',
  'gpt-5.6-luna': 'GPT 5.6 Luna',
};

// Text/analysis surfaces own provider, model, and reasoning in one row. Live
// voice surfaces are the deliberate exception: each row selects the native
// model for one direct API while provider order stays in VoiceProviderPanel.
const SECTIONS = [
  {
    id: 'conversation',
    title: 'گفتگو و دانش',
    description: 'مدل‌های متنی مورد استفاده توسط مشاور هوشمند و بخش دانش.',
    surfaces: [
      { key: 'consult_chat', label: 'چت متنی مشتری',         icon: MessageSquare, hint: 'پاسخ به سوالات مشتری در صفحهٔ مشاوره.' },
      { key: 'teach_chat',   label: 'چت آموزش ادمین',        icon: GraduationCap, hint: 'مدل کلاس درس ادمین (ساخت/ویرایش پایگاه دانش).' },
      { key: 'summary',      label: 'خلاصه‌سازی پس از تماس', icon: FileSearch,    hint: 'خلاصهٔ خودکار مکالمات صوتی پس از پایان تماس.' },
      { key: 'distill',      label: 'استخراج گزاره از سند', icon: Sparkles, hint: 'استخراج نکات ساختاریافته از PDF/تصویر/صدا.' },
      { key: 'dreaming',     label: 'تثبیت دانش',            icon: Sparkles, hint: 'پاک‌سازی و یکپارچه‌سازی پایگاه دانش در پس‌زمینه.' },
    ],
  },
  {
    id: 'live-voice',
    title: 'مکالمهٔ زنده',
    description: 'مدل‌های بومی هر API برای جریان صوتی مستقیم، مستقل از OpenRouter و اشتراک ChatGPT.',
    surfaces: [
      { key: 'consult_voice_openai', label: 'مدل بلادرنگ OpenAI', icon: Mic2, directProvider: 'OpenAI مستقیم',
        hint: 'مدل API مستقیم OpenAI برای تماس زنده؛ ترتیب ارائه‌دهندهٔ اصلی و پشتیبان در تنظیمات مشاور صوتی تعیین می‌شود.' },
      { key: 'consult_voice_gemini', label: 'مدل زنده Gemini', icon: Mic2, directProvider: 'Gemini مستقیم',
        hint: 'مدل API مستقیم Gemini برای تماس زنده؛ ترتیب ارائه‌دهندهٔ اصلی و پشتیبان در تنظیمات مشاور صوتی تعیین می‌شود.' },
    ],
  },
  {
    id: 'vision',
    title: 'بینایی و استخراج',
    description: 'مدل‌هایی که مستقیماً پیکسل‌های تصویر را می‌خوانند (مثلاً وقتی EXIF عکس خالی است).',
    surfaces: [
      { key: 'stamp_gps_extraction', label: 'خواندن مهر موقعیت روی عکس', icon: Camera,
        hint: 'وقتی EXIF عکس خالی باشد، مختصات را از کادر مهر روی پیکسل می‌خواند.',
        recommendedDescription: 'برای متن مهر کافی و بسیار ارزان است.' },
      { key: 'resume_scoring', label: 'نمره‌دهی رزومه نیروی اجرایی', icon: Star,
        hint: 'رزومه نیروی اجرایی را هنگام ثبت‌نام می‌خواند و نمره اولیه می‌دهد.',
        recommendedDescription: 'Gemini 3.5 Flash برای رزومه فارسی/انگلیسی کافی است.' },
    ],
  },
  {
    id: 'design',
    title: 'طراحی شبکه',
    description: 'توضیح و گزارشِ خودکارِ طراحیِ FTTH، بر پایه‌ی قوانینِ بررسی‌شده‌ی موتور (نه حدس).',
    surfaces: [
      { key: 'design_element_explanation', label: 'توضیحِ جای‌گذاریِ المان‌ها', icon: Network,
        hint: 'برای هر المان، تحلیلِ قانون‌محور تولید می‌کند که در «حالتِ تفصیلیِ» نقشه دیده می‌شود.' },
      { key: 'design_profile_interview', label: 'مصاحبه‌ی قوانین پروفایل', icon: ClipboardList,
        hint: 'نقشه‌کردن قواعد اپراتور روی گزینه‌های موتور طراحی' },
      { key: 'design_report', label: 'گزارشِ کاملِ طراحی', icon: ClipboardList,
        hint: 'گزارش هوشمند طرح' },
    ],
  },
];

// Operational order is part of the inspection contract. Full-council members
// are listed before their chairman, then strike members/chairman, then solo.
const INSPECTION_ROWS = [
  {
    variant: 'full', index: 0, key: INSPECTION_SLOT_KEYS.full[0],
    label: 'شورای نظارت - عضو اول',
    hint: 'عضو اول شورای نظارت؛ مدل و سطح تفکر در تحلیل اولیه و بازبینی همتا استفاده می‌شود.',
  },
  {
    variant: 'full', index: 1, key: INSPECTION_SLOT_KEYS.full[1],
    label: 'شورای نظارت - عضو دوم',
    hint: 'عضو دوم شورای نظارت؛ مدل و سطح تفکر در تحلیل اولیه و بازبینی همتا استفاده می‌شود.',
  },
  {
    variant: 'full', index: 2, key: INSPECTION_SLOT_KEYS.full[2],
    label: 'شورای نظارت - عضو سوم',
    hint: 'عضو سوم شورای نظارت؛ مدل و سطح تفکر در تحلیل اولیه و بازبینی همتا استفاده می‌شود.',
  },
  {
    variant: 'full', index: 3, key: INSPECTION_SLOT_KEYS.full[3],
    label: 'شورای نظارت - رئیس شورا',
    hint: 'رئیس شورای نظارت؛ رأی اعضا را جمع‌بندی و حکم نهایی را صادر می‌کند.',
  },
  {
    variant: 'strike', index: 0, key: INSPECTION_SLOT_KEYS.strike[0],
    label: 'شورای ضربت - عضو اول',
    hint: 'عضو اول شورای ضربت؛ تحلیل سریع و مستقل شواهد را انجام می‌دهد.',
  },
  {
    variant: 'strike', index: 1, key: INSPECTION_SLOT_KEYS.strike[1],
    label: 'شورای ضربت - عضو دوم',
    hint: 'عضو دوم شورای ضربت؛ تحلیل سریع و مستقل شواهد را انجام می‌دهد.',
  },
  {
    variant: 'strike', index: 2, key: INSPECTION_SLOT_KEYS.strike[2],
    label: 'شورای ضربت - رئیس شورا',
    hint: 'رئیس شورای ضربت؛ تحلیل دو عضو را به نتیجه نهایی تبدیل می‌کند.',
  },
  {
    variant: 'solo', index: 0, key: INSPECTION_SLOT_KEYS.solo[0],
    label: 'ارتش تک‌نفره - بازرس',
    hint: 'بازرس ارتش تک‌نفره؛ تحلیل و رأی نهایی را در یک مرحله انجام می‌دهد.',
  },
];

const SAVE_GROUPS = {
  models: 'مدل‌های غیر بازرسی',
  providers: 'ارائه‌دهندگان',
  subscription: 'اشتراک ChatGPT',
  reasoning: 'سطح تفکر غیر بازرسی',
  design: 'توضیح طراحی',
  inspection: 'نظارت و بازرسی',
};

function copyInspectionSettings(bundle) {
  return {
    concealModelIdentities: bundle.concealModelIdentities,
    variants: Object.fromEntries(Object.keys(INSPECTION_SLOT_KEYS).map((variant) => [
      variant,
      bundle.variants[variant].map((slot) => ({ ...slot })),
    ])),
  };
}

async function requireSuccessfulSave(response) {
  if (response.ok) return response;
  const body = await response.json().catch(() => ({}));
  throw new Error(body.error || 'خطا در ذخیره');
}

function quotedGroups(labels) {
  return labels.map((label) => `«${label}»`).join('، ');
}

async function authHeaders(json = false) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return null;
  const h = { Authorization: `Bearer ${session.access_token}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

export default function ModelSettingsPanel() {
  const {
    effectiveSettings: effectiveInspectionSettings,
    loading: inspectionLoading,
    error: inspectionError,
    saveInspectionSettings,
  } = useInspectionSettings();
  const [settings, setSettings] = useState({});          // surface -> model id
  const modelChangesRef = useRef({});
  const [providers, setProviders] = useState({});        // surface -> { provider, eligible }
  const [subCfg, setSubCfg] = useState({ model: 'gpt-6.1-sol', models: [], surfaces: {} });
  const [reasoning, setReasoning] = useState({});        // surface -> effort
  const [reasoningEfforts, setReasoningEfforts] = useState(EFFORTS_FALLBACK);
  const [designEnabled, setDesignEnabled] = useState(true);
  const [inspectionDraft, setInspectionDraft] = useState(null);
  const [loadedGroups, setLoadedGroups] = useState({});
  const [dirtyGroups, setDirtyGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      const h = await authHeaders();
      if (!h) { setLoading(false); return; }
      const results = await Promise.allSettled([
        fetch('/api/admin/model-settings', { headers: h }),
        fetch('/api/admin/provider-settings', { headers: h }),
        fetch('/api/admin/subscription-config', { headers: h }),
        fetch('/api/admin/design-explanation-settings', { headers: h }),
        fetch('/api/admin/reasoning-settings', { headers: h }),
      ]);
      const load = async (index, apply) => {
        const result = results[index];
        if (result.status !== 'fulfilled' || !result.value.ok) return false;
        try {
          apply(await result.value.json());
          return true;
        } catch (_error) {
          return false;
        }
      };
      const loaded = {};
      loaded.models = await load(0, setSettings);
      loaded.providers = await load(1, setProviders);
      loaded.subscription = await load(2, (value) => setSubCfg({ ...value, surfaces: value.surfaces || {} }));
      loaded.design = await load(3, (value) => setDesignEnabled(value.enabled !== false));
      loaded.reasoning = await load(4, (value) => {
        setReasoning(value.settings || {});
        if (Array.isArray(value.efforts) && value.efforts.length) {
          setReasoningEfforts(value.efforts);
        }
      });
      setLoadedGroups((previous) => ({ ...previous, ...loaded }));
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (inspectionError) {
      setLoadedGroups((previous) => ({ ...previous, inspection: false }));
    } else if (effectiveInspectionSettings) {
      setInspectionDraft(copyInspectionSettings(effectiveInspectionSettings));
      setLoadedGroups((previous) => ({ ...previous, inspection: true }));
    }
  }, [effectiveInspectionSettings, inspectionError]);

  const markDirty = useCallback((group) => {
    setDirtyGroups((previous) => ({ ...previous, [group]: true }));
  }, []);

  const onModel = useCallback((surface, modelId) => {
    setSettings((prev) => ({ ...prev, [surface]: modelId }));
    const next = { ...modelChangesRef.current, [surface]: modelId };
    modelChangesRef.current = next;
    markDirty('models');
  }, [markDirty]);
  const onProvider = useCallback((surface, provider) => {
    setProviders((prev) => ({ ...prev, [surface]: { ...(prev[surface] || {}), provider } }));
    markDirty('providers');
  }, [markDirty]);
  const onReasoning = useCallback((surface, effort) => {
    setReasoning((prev) => ({ ...prev, [surface]: effort }));
    markDirty('reasoning');
  }, [markDirty]);

  const onSubscriptionModel = useCallback((model) => {
    setSubCfg((previous) => ({ ...previous, model }));
    markDirty('subscription');
  }, [markDirty]);

  const onSubscriptionSurface = useCallback((surface, model) => {
    setSubCfg((previous) => ({ ...previous, surfaces: { ...previous.surfaces, [surface]: model } }));
    markDirty('subscription');
  }, [markDirty]);

  const onDesignToggle = useCallback(() => {
    setDesignEnabled((previous) => !previous);
    markDirty('design');
  }, [markDirty]);

  const onInspectionConcealmentToggle = useCallback(() => {
    setInspectionDraft((previous) => ({
      ...previous,
      concealModelIdentities: !previous.concealModelIdentities,
    }));
    markDirty('inspection');
  }, [markDirty]);

  const onInspectionSetting = useCallback((variant, index, field, value) => {
    setInspectionDraft((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        variants: {
          ...previous.variants,
          [variant]: previous.variants[variant].map((slot, slotIndex) => (
            slotIndex === index ? { ...slot, [field]: value } : slot
          )),
        },
      };
    });
    markDirty('inspection');
  }, [markDirty]);

  const onSave = useCallback(async () => {
    const submittedModelChanges = { ...modelChangesRef.current };
    setSaving(true);
    setToast(null);
    try {
      const jh = await authHeaders(true);
      const providerBody = {};
      Object.entries(providers).forEach(([k, v]) => { providerBody[k] = v.provider || 'openrouter'; });
      const operations = [
        {
          key: 'models',
          label: SAVE_GROUPS.models,
          save: () => fetch('/api/admin/model-settings', {
            method: 'PUT', headers: jh, body: JSON.stringify(submittedModelChanges),
          }).then(requireSuccessfulSave),
        },
        {
          key: 'providers',
          label: SAVE_GROUPS.providers,
          save: () => fetch('/api/admin/provider-settings', {
            method: 'PUT', headers: jh, body: JSON.stringify(providerBody),
          }).then(requireSuccessfulSave),
        },
        {
          key: 'subscription',
          label: SAVE_GROUPS.subscription,
          save: () => fetch('/api/admin/subscription-config', {
            method: 'PUT', headers: jh, body: JSON.stringify({ model: subCfg.model, surfaces: subCfg.surfaces }),
          }).then(requireSuccessfulSave),
        },
        {
          key: 'reasoning',
          label: SAVE_GROUPS.reasoning,
          save: () => fetch('/api/admin/reasoning-settings', {
            method: 'PUT', headers: jh, body: JSON.stringify(reasoning),
          }).then(requireSuccessfulSave),
        },
        {
          key: 'design',
          label: SAVE_GROUPS.design,
          save: () => fetch('/api/admin/design-explanation-settings', {
            method: 'PUT', headers: jh, body: JSON.stringify({ enabled: designEnabled }),
          }).then(requireSuccessfulSave),
        },
        {
          key: 'inspection',
          label: SAVE_GROUPS.inspection,
          save: () => (inspectionDraft
            ? saveInspectionSettings({
              concealModelIdentities: inspectionDraft.concealModelIdentities,
              variants: inspectionDraft.variants,
            })
            : Promise.reject(new Error('تنظیمات نظارت و بازرسی در دسترس نیست.'))),
        },
      ].filter(({ key }) => loadedGroups[key] && dirtyGroups[key]);
      const outcomes = await Promise.allSettled(operations.map(({ save }) => save()));
      const failed = operations.flatMap((operation, index) => (
        outcomes[index].status === 'rejected'
          ? [{ ...operation, reason: outcomes[index].reason }]
          : []
      ));
      const succeeded = operations.filter((_operation, index) => outcomes[index].status === 'fulfilled');

      const modelsSaved = succeeded.some(({ key }) => key === 'models');
      let hasNewerUnsavedModelChanges = false;
      if (modelsSaved) {
        const remainingModelChanges = Object.fromEntries(
          Object.entries(modelChangesRef.current).filter(([key, value]) => (
            submittedModelChanges[key] !== value
          )),
        );
        modelChangesRef.current = remainingModelChanges;
        hasNewerUnsavedModelChanges = Object.keys(remainingModelChanges).length > 0;
      }

      if (succeeded.length) {
        setDirtyGroups((previous) => {
          const next = { ...previous };
          succeeded.forEach(({ key }) => {
            if (key !== 'models' || !hasNewerUnsavedModelChanges) delete next[key];
          });
          return next;
        });
      }

      if (failed.length === 0) {
        setToast({
          kind: 'ok',
          text: hasNewerUnsavedModelChanges
            ? 'ذخیره انجام شد؛ تغییر جدیدتری هنوز ذخیره نشده است'
            : 'تنظیمات ذخیره شد',
        });
      } else {
        const conflict = failed.find(({ reason }) => (
          reason?.code === 'INSPECTION_SETTINGS_CONFLICT'
        ));
        const failedText = conflict
          ? conflict.reason.message
          : `ذخیره ${quotedGroups(failed.map(({ label }) => label))} ناموفق بود.`;
        const succeededText = succeeded.length
          ? ` ${quotedGroups(succeeded.map(({ label }) => label))} ذخیره شدند.`
          : '';
        setToast({ kind: 'error', text: failedText + succeededText });
      }
    } catch {
      setToast({ kind: 'error', text: 'ذخیره ناموفق بود' });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 4000);
    }
  }, [
    designEnabled,
    dirtyGroups,
    inspectionDraft,
    loadedGroups,
    providers,
    reasoning,
    saveInspectionSettings,
    subCfg,
  ]);

  const surfaceState = useMemo(() => {
    const map = {};
    SECTIONS.forEach((sec) => {
      sec.surfaces.forEach(({ key }) => {
        const req = SURFACE_REQUIREMENTS[key] || {};
        const options = modelsForSurface(key);
        const lockedTo = req.lockedTo || null;
        const recommended = req.defaultModel || null;
        const selectedId = lockedTo || settings[key] || recommended || '';
        const selected = AI_MODELS.find((m) => m.id === selectedId) || null;
        map[key] = { options, lockedTo, recommended, selectedId, selected };
      });
    });
    return map;
  }, [settings]);

  // provider_inspection: GPT slots go subscription-first unless set to openrouter.
  const inspectionOnSub = Boolean(providers.inspection) && providers.inspection.provider !== 'openrouter';

  if (loading) {
    return <div className="settings-loading"><Loader2 className="spin" /> در حال بارگذاری…</div>;
  }

  return (
    <div className="model-settings-panel" dir="rtl">
      <div className="settings-callout">
        <Info size={16} />
        <div>
          ردیف‌های متنی و تحلیلی سه تنظیم دارند: <b>ارائه‌دهنده</b> (OpenRouter یا اشتراکِ ChatGPT)، <b>مدل</b> و <b>سطحِ تفکر</b>.
          هر ردیف مدلِ ارائه‌دهنده‌ی انتخاب‌شده را دارد. در حالتِ <b>«اشتراکِ ChatGPT»</b>،
          مدلِ سراسریِ کارتِ زیر پیش‌فرض است. «مدل اشتراک» در هر ردیف می‌تواند جای آن را بگیرد
          و «مدل پشتیبان OpenRouter» همان ردیف هنگام خطای اشتراک پاسخ می‌دهد.
          ولی <b>سطحِ تفکر برای هر بخش جداگانه</b> روی هر دو ارائه‌دهنده اعمال می‌شود؛ گزینهٔ
          <b> «هوشمند»</b> یعنی مدل خودش عمقِ تفکر را انتخاب می‌کند (برای کارهای ساده کم، برای کارهای سخت زیاد).
          ردیف‌های مکالمهٔ زنده مدل بومی هر ارائه‌دهنده را مستقیم از API همان ارائه‌دهنده انتخاب می‌کنند؛
          چیدمان اصلی/پشتیبان همچنان در تنظیمات مشاور صوتی است و سطح تفکر اینجا تنظیم نمی‌شود.
          تغییرات ظرف ۶۰ ثانیه اعمال می‌شود.
        </div>
      </div>

      <div className="sub-config-card">
        <div className="sub-config-title"><Cpu size={16} /> اشتراکِ ChatGPT</div>
        <p className="sub-config-desc">
          این مدل پیش‌فرضِ سراسریِ «اشتراکِ ChatGPT» است. هر ردیف می‌تواند مدلِ اشتراکِ جداگانه داشته باشد.
          سطحِ تفکر در همان ردیف تنظیم می‌شود.
        </p>
        <div className="sub-config-row">
          <div className="sub-config-field">
            <label>مدلِ اشتراک</label>
            <select className="settings-select" value={subCfg.model || 'gpt-6.1-sol'}
              onChange={(e) => onSubscriptionModel(e.target.value)}>
              {(subCfg.models || ['gpt-6.1-sol']).map((m) => (
                <option key={m} value={m}>{toFa(SUBMODEL_LABELS[m] || m)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {SECTIONS.map((section) => (
        <section className="settings-section" key={section.id}>
          <header className="settings-section-header"
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 className="settings-section-title">{section.title}</h4>
              <p className="settings-section-description">{section.description}</p>
            </div>
            {section.id === 'design' && (
              <button type="button" className="settings-switch" onClick={onDesignToggle}
                aria-pressed={designEnabled} title="روشن/خاموش کردنِ موتورِ توضیحِ طراحی">
                <span className={`settings-switch-track ${designEnabled ? 'on' : ''}`}>
                  <span className="settings-switch-knob" />
                </span>
                <span className="settings-switch-label">{designEnabled ? 'روشن' : 'خاموش'}</span>
              </button>
            )}
          </header>

          <div className="settings-grid">
            {section.surfaces.map(({
              key, label, icon: Icon, hint, recommendedDescription, directProvider,
            }) => {
              const { options, lockedTo, recommended, selectedId, selected } = surfaceState[key];
              const prov = providers[key] || { provider: 'openrouter', eligible: false };
              const onSub = !directProvider && prov.provider === 'openai-subscription';
              const isRecommended = recommended && selectedId === recommended;
              // On the subscription the OpenRouter model is the backup, so it
              // stays visible and editable next to the subscription model.
              const openRouterSelect = <>
                {selected && <ModelLogo logoComponent={selected.logoComponent} size={18} />}
                <select
                  className="settings-select"
                  aria-label={`${label} - ${onSub ? 'مدل پشتیبان OpenRouter' : 'مدل'}`}
                  value={selectedId || ''}
                  onChange={(e) => onModel(key, e.target.value)}
                  disabled={Boolean(lockedTo)}
                  title={lockedTo ? 'این سطح به‌خاطر نیاز به ورودی چندحالته قفل شده است.' : undefined}
                >
                  {options.map((m) => (
                    <option key={m.id} value={m.id} disabled={!m.compatible}>
                      {m.label}{!m.compatible ? ' - ناسازگار' : ''}
                      {recommended && m.id === recommended ? ' ★' : ''}
                    </option>
                  ))}
                </select>
              </>;
              return (
                <div className="settings-row" key={key}>
                  <div className="settings-label-block">
                    <div className="settings-label">
                      {Icon && <Icon size={15} className="settings-row-icon" aria-hidden />}
                      <span>{label}</span>
                      {lockedTo && (
                        <span className="settings-lock-badge" title={`قفل‌شده روی ${MODEL_BY_ID[lockedTo]?.label || lockedTo}`}>
                          <Lock size={11} /> قفل‌شده
                        </span>
                      )}
                      {isRecommended && !lockedTo && (
                        <span className="settings-recommended-badge" title={recommendedDescription || 'توصیه‌شده برای این بخش'}>
                          <Star size={11} fill="currentColor" /> توصیه‌شده
                        </span>
                      )}
                    </div>
                    {hint && <div className="settings-row-hint">{hint}</div>}
                  </div>

                  <div className="settings-controls">
                    <div className="settings-field field-provider">
                      <span className="settings-field-label">ارائه‌دهنده</span>
                      {directProvider ? (
                        <span className="settings-provider-static" title="اتصال مستقیم به API ارائه‌دهنده">
                          {directProvider}
                        </span>
                      ) : prov.eligible ? (
                        <div className="settings-provider" role="group" aria-label="ارائه‌دهنده">
                          <button type="button" className={!onSub ? 'on' : ''}
                            onClick={() => onProvider(key, 'openrouter')}>OpenRouter</button>
                          <button type="button" className={onSub ? 'on' : ''}
                            onClick={() => onProvider(key, 'openai-subscription')}>اشتراکِ ChatGPT</button>
                        </div>
                      ) : (
                        <span className="settings-provider-static" title="این بخش فقط OpenRouter را پشتیبانی می‌کند">OpenRouter</span>
                      )}
                    </div>

                    <div className="settings-field field-model">
                      <span className="settings-field-label">{onSub ? 'مدل اشتراک' : 'مدل'}</span>
                      <div className="settings-select-wrap">
                        {onSub ? (
                          <select className="settings-select" aria-label={`${label} - مدل اشتراک`}
                            value={subCfg.surfaces[key] || ''}
                            onChange={(e) => onSubscriptionSurface(key, e.target.value)}>
                            <option value="">پیش‌فرض (سراسری)</option>
                            {subCfg.models.map((m) => (
                              <option key={m} value={m}>{toFa(SUBMODEL_LABELS[m] || m)}</option>
                            ))}
                          </select>
                        ) : openRouterSelect}
                      </div>
                      {onSub && !subCfg.surfaces[key] && <span className="settings-field-note">
                        مدلِ سراسری: {toFa(SUBMODEL_LABELS[subCfg.model] || subCfg.model)}
                      </span>}
                    </div>

                    {onSub && (
                      <div className="settings-field field-model">
                        <span className="settings-field-label">مدل پشتیبان OpenRouter</span>
                        <div className="settings-select-wrap">{openRouterSelect}</div>
                      </div>
                    )}

                    {!directProvider && (
                      <div className="settings-field field-reasoning">
                        <span className="settings-field-label"
                          title="عمقِ تفکر (reasoning) برای این بخش - روی هر دو ارائه‌دهنده اعمال می‌شود. «هوشمند» = مدل خودش تصمیم می‌گیرد.">
                          سطحِ تفکر
                        </span>
                        <select
                          className="settings-select settings-reasoning-select"
                          value={reasoning[key] || 'adaptive'}
                          onChange={(e) => onReasoning(key, e.target.value)}
                        >
                          {reasoningEfforts.map((ef) => (
                            <option key={ef} value={ef}>{EFFORT_FA[ef] || ef}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <section className="settings-section settings-inspection-section">
        <header className="settings-section-header settings-inspection-header">
          <div className="settings-inspection-heading">
            <h4 className="settings-section-title">نظارت و بازرسی</h4>
            <p className="settings-section-description">
              مدل و عمق تفکر هر نقش را به‌ترتیب اجرای شورای نظارت، شورای ضربت و بازرس تک‌نفره تعیین کنید.
            </p>
          </div>
          {providers.inspection && (
            <button
              type="button"
              className="settings-switch settings-inspection-switch"
              role="switch"
              aria-label="اشتراک Codex برای مدل‌های GPT"
              aria-checked={inspectionOnSub}
              onClick={() => onProvider('inspection', inspectionOnSub ? 'openrouter' : 'openai-subscription')}
            >
              <span className={`settings-switch-track ${inspectionOnSub ? 'on' : ''}`} aria-hidden="true">
                <span className="settings-switch-knob" />
              </span>
              <span className="settings-switch-copy">
                <span className="settings-switch-label">اشتراک Codex برای مدل‌های GPT</span>
                <span className="settings-switch-note">
                  {inspectionOnSub ? 'فعال؛ پشتیبان همان مدل در OpenRouter' : 'غیرفعال؛ همهٔ نقش‌ها از OpenRouter'}
                </span>
              </span>
            </button>
          )}
          {inspectionDraft && (
            <button
              type="button"
              className="settings-switch settings-inspection-switch"
              role="switch"
              aria-label="پنهان‌سازی هویت مدل‌ها در تب نظارت"
              aria-checked={inspectionDraft.concealModelIdentities}
              disabled={inspectionLoading}
              onClick={onInspectionConcealmentToggle}
            >
              <span
                className={`settings-switch-track ${inspectionDraft.concealModelIdentities ? 'on' : ''}`}
                aria-hidden="true"
              >
                <span className="settings-switch-knob" />
              </span>
              <span className="settings-switch-copy">
                <span className="settings-switch-label">پنهان‌سازی هویت مدل‌ها در تب نظارت</span>
                <span className="settings-switch-note">
                  {inspectionDraft.concealModelIdentities ? 'فعال' : 'غیرفعال'}
                </span>
              </span>
            </button>
          )}
        </header>

        {inspectionDraft ? (
          <div className="settings-grid settings-inspection-grid">
            {INSPECTION_ROWS.map(({ variant, index, key, label, hint }) => {
              const slot = inspectionDraft.variants[variant][index];
              const options = modelsForSurface(key);
              const selected = MODEL_BY_ID[slot.model];
              return (
                <div
                  className="settings-row inspection-settings-row"
                  key={key}
                  role="group"
                  aria-label={label}
                  data-inspection-slot={key}
                >
                  <div className="settings-label-block">
                    <div className="settings-label">
                      <span>{label}</span>
                    </div>
                    <div className="settings-row-hint">{hint}</div>
                  </div>

                  <div className="settings-controls">
                    <div className="settings-field field-provider">
                      <span className="settings-field-label">ارائه‌دهنده</span>
                      <span className="settings-provider-static">
                        {inspectionOnSub && selected.capabilities.subscriptionModel
                          ? 'اشتراک Codex، پشتیبان OpenRouter'
                          : 'OpenRouter'}
                      </span>
                    </div>

                    <div className="settings-field field-model">
                      <span className="settings-field-label settings-model-field-label">
                        <span>مدل واقعی</span>
                        <span className="settings-model-vendor">{selected.vendor}</span>
                      </span>
                      <div className="settings-select-wrap">
                        <ModelLogo logoComponent={selected.logoComponent} size={18} />
                        <select
                          className="settings-select"
                          aria-label={`${label} - مدل`}
                          value={slot.model}
                          onChange={(event) => onInspectionSetting(
                            variant, index, 'model', event.target.value,
                          )}
                        >
                          {options.map((model) => (
                            <option key={model.id} value={model.id} disabled={!model.compatible}>
                              {model.label}{!model.compatible ? ' - ناسازگار' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="settings-field field-reasoning">
                      <span className="settings-field-label">سطح تفکر</span>
                      <select
                        className="settings-select settings-reasoning-select"
                        aria-label={`${label} - سطح تفکر`}
                        value={slot.reasoningEffort}
                        onChange={(event) => onInspectionSetting(
                          variant, index, 'reasoningEffort', event.target.value,
                        )}
                      >
                        {reasoningEfforts.map((effort) => (
                          <option key={effort} value={effort}>{EFFORT_FA[effort] || effort}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className={`settings-inspection-state ${inspectionError ? 'is-error' : ''}`}
            role={inspectionError ? 'alert' : 'status'}
          >
            {inspectionError || 'در حال دریافت تنظیمات نظارت و بازرسی…'}
          </div>
        )}
      </section>

      <div className="settings-actions">
        <motion.button type="button" className="settings-save-btn" onClick={onSave}
          disabled={saving} whileTap={!saving ? { scale: 0.98 } : undefined}>
          {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
          ذخیره تنظیمات
        </motion.button>
        {toast && (
          <div
            className={`settings-toast settings-toast-${toast.kind}`}
            role={toast.kind === 'error' ? 'alert' : 'status'}
          >
            {toast.kind === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            <span>{toast.text}</span>
          </div>
        )}
      </div>
    </div>
  );
}
